import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Bell,
  Check,
  Clock,
  ShoppingBag,
  FileText,
  Download,
  Edit,
  Save,
  ArrowLeft,
  Plus,
  Trash2
} from "lucide-react";
import api from "@/lib/api";

export default function ClientDetailPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [newReminder, setNewReminder] = useState({
    reminder_type: "custom",
    due_date: "",
    notes: ""
  });

  useEffect(() => {
    loadClient();
  }, [clientId]);

  const loadClient = async () => {
    try {
      const response = await api.get(`/clients/${clientId}`);
      setClient(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error("Client non trouvé");
      navigate("/clients");
    } finally {
      setLoading(false);
    }
  };

  const saveChanges = async () => {
    try {
      await api.put(`/clients/${clientId}`, {
        name: editData.name,
        phone: editData.phone,
        email: editData.email,
        address: editData.address,
        id_number: editData.id_number,
        notes: editData.notes
      });
      setClient(editData);
      setEditing(false);
      toast.success("Client mis à jour!");
    } catch (error) {
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const addReminder = async () => {
    if (!newReminder.due_date) {
      toast.error("Date requise");
      return;
    }
    
    try {
      await api.post(`/clients/${clientId}/reminders`, newReminder);
      toast.success("Rappel ajouté!");
      setShowAddReminder(false);
      setNewReminder({ reminder_type: "custom", due_date: "", notes: "" });
      loadClient();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const completeReminder = async (reminderId) => {
    try {
      await api.put(`/reminders/${reminderId}/complete`);
      toast.success("Rappel terminé");
      loadClient();
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const deleteClient = async () => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) return;
    
    try {
      await api.delete(`/clients/${clientId}`);
      toast.success("Client supprimé");
      navigate("/clients");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse text-muted-foreground">Chargement...</div>
        </div>
      </Layout>
    );
  }

  if (!client) return null;

  return (
    <Layout>
      <div className="p-4 md:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/clients")}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-heading font-bold">{client.name}</h1>
              <Badge variant="outline" className="font-mono">{client.client_id}</Badge>
            </div>
            <p className="text-muted-foreground text-sm">
              Client depuis {new Date(client.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <div className="flex gap-2">
            {editing ? (
              <>
                <Button variant="outline" onClick={() => setEditing(false)}>
                  Annuler
                </Button>
                <Button onClick={saveChanges} className="bg-primary text-black">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setEditing(true)}>
                  <Edit className="w-4 h-4 mr-2" />
                  Modifier
                </Button>
                <Button variant="destructive" size="icon" onClick={deleteClient}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Client Info */}
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-primary" />
                  Informations
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Nom</Label>
                        <Input
                          value={editData.name}
                          onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Téléphone</Label>
                        <Input
                          value={editData.phone}
                          onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={editData.email || ""}
                          onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label>N° Identification</Label>
                        <Input
                          value={editData.id_number || ""}
                          onChange={(e) => setEditData({ ...editData, id_number: e.target.value })}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Adresse</Label>
                        <Input
                          value={editData.address || ""}
                          onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label>Notes</Label>
                        <Textarea
                          value={editData.notes || ""}
                          onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                          rows={3}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Phone className="w-4 h-4 text-muted-foreground" />
                      <a href={`tel:${client.phone}`} className="hover:text-primary">
                        {client.phone}
                      </a>
                    </div>
                    {client.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <a href={`mailto:${client.email}`} className="hover:text-primary">
                          {client.email}
                        </a>
                      </div>
                    )}
                    {client.address && (
                      <div className="flex items-center gap-3">
                        <MapPin className="w-4 h-4 text-muted-foreground" />
                        <span>{client.address}</span>
                      </div>
                    )}
                    {client.id_number && (
                      <div className="flex items-center gap-3">
                        <FileText className="w-4 h-4 text-muted-foreground" />
                        <span>ID: {client.id_number}</span>
                      </div>
                    )}
                    {client.notes && (
                      <div className="mt-4 p-3 bg-muted/30 rounded-lg">
                        <p className="text-sm text-muted-foreground">{client.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Purchase History */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShoppingBag className="w-5 h-5 text-primary" />
                  Historique d'Achats
                </CardTitle>
                <CardDescription>
                  {client.purchases?.length || 0} achat(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {client.purchases?.length > 0 ? (
                  <div className="space-y-3">
                    {client.purchases.map((purchase, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{purchase.scooter_name}</p>
                          <p className="text-sm text-muted-foreground">
                            {purchase.sale_date} • {purchase.sale_price} EUR
                          </p>
                        </div>
                        <div className="flex gap-2">
                          {purchase.documents?.map((doc, docIndex) => (
                            <Button
                              key={docIndex}
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/stock/${purchase.scooter_id}`)}
                            >
                              <FileText className="w-4 h-4" />
                            </Button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4">
                    Aucun achat enregistré
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Reminders */}
          <div>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-primary" />
                    Rappels
                  </CardTitle>
                  <Dialog open={showAddReminder} onOpenChange={setShowAddReminder}>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Nouveau Rappel</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div>
                          <Label>Date</Label>
                          <Input
                            type="date"
                            value={newReminder.due_date}
                            onChange={(e) => setNewReminder({ ...newReminder, due_date: e.target.value })}
                          />
                        </div>
                        <div>
                          <Label>Notes</Label>
                          <Textarea
                            value={newReminder.notes}
                            onChange={(e) => setNewReminder({ ...newReminder, notes: e.target.value })}
                            placeholder="Description du rappel..."
                            rows={3}
                          />
                        </div>
                        <Button onClick={addReminder} className="w-full bg-primary text-black">
                          Ajouter
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {client.reminders?.length > 0 ? (
                  <div className="space-y-3">
                    {client.reminders
                      .sort((a, b) => a.completed - b.completed || new Date(a.due_date) - new Date(b.due_date))
                      .map((reminder, index) => (
                        <div
                          key={index}
                          className={`p-3 rounded-lg border ${
                            reminder.completed
                              ? 'bg-muted/20 border-muted'
                              : new Date(reminder.due_date) <= new Date()
                              ? 'bg-orange-500/10 border-orange-500/30'
                              : 'bg-muted/30 border-border'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                {reminder.reminder_type === 'call_48h' && (
                                  <Phone className="w-3 h-3 text-blue-500" />
                                )}
                                {reminder.reminder_type === 'monthly_service' && (
                                  <Calendar className="w-3 h-3 text-green-500" />
                                )}
                                {reminder.reminder_type === 'custom' && (
                                  <Bell className="w-3 h-3 text-purple-500" />
                                )}
                                <span className={`text-sm font-medium ${reminder.completed ? 'line-through text-muted-foreground' : ''}`}>
                                  {reminder.reminder_type === 'call_48h' && 'Appel 48h'}
                                  {reminder.reminder_type === 'monthly_service' && 'Révision mensuelle'}
                                  {reminder.reminder_type === 'custom' && 'Rappel personnalisé'}
                                </span>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                <Clock className="w-3 h-3 inline mr-1" />
                                {reminder.due_date}
                              </p>
                              {reminder.notes && (
                                <p className="text-xs text-muted-foreground mt-1">{reminder.notes}</p>
                              )}
                            </div>
                            {!reminder.completed && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => completeReminder(reminder.id)}
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-4 text-sm">
                    Aucun rappel
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-sm">Actions Rapides</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => window.open(`tel:${client.phone}`)}
                >
                  <Phone className="w-4 h-4 mr-2 text-green-500" />
                  Appeler
                </Button>
                {client.email && (
                  <Button
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => window.open(`mailto:${client.email}`)}
                  >
                    <Mail className="w-4 h-4 mr-2 text-blue-500" />
                    Envoyer un email
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  onClick={() => {
                    const msg = `Bonjour ${client.name}, nous vous contactons de Scutersh...`;
                    window.open(`https://wa.me/${client.phone.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(msg)}`);
                  }}
                >
                  <svg className="w-4 h-4 mr-2 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
